import BaseModalForm from 'components/BaseModalForm'
import _ from 'lodash'
import React, { forwardRef, useCallback, useMemo, useState } from 'react'
import ToolsIntegrationsAPI from 'restAPI/ToolsIntegrationAPI';
import ModalUtil from 'utils/modalUtil';
import { FieldTypes, nameRegexValidator } from 'utils/formUtils';
import { string as stringPropType, array as arrayPropType } from "prop-types";
import { hasPermission, isSuperOrTenantAdmin } from 'utils/authService';
import { PermissionSetMap } from 'constants/RBACConstants';
import { useFeatureFlag } from 'contexts/FeatureFlagContext';
import { AutoMappingSupportedTools } from './new/util';
import { useAutoFocus } from 'hooks/useFocusHelpers';

const shareableTools = ["Snyk", "Checkmarx", "Dependabot", "Blackduck Hub", "HackerOne", "JFrog Vision (Vdoo)", "Coverity on Polaris"];

/**
 * @function getFields 
 * Optional - It defines the getFields function which specifies the custom fields to be rendered as needed
 * e.g, filtering out the fields which are not required
 */

const GenericPullCredentialsForm = forwardRef(function GenericPullCredentialsForm({prependFields = [], getFields, ...props}, ref){
    const isEdit = !_.isUndefined(props.config);
    const [success, setSuccess] = useState(false);
    const featureFlags = useFeatureFlag();
    const nameInputFieldRef = useAutoFocus();

    const [isShareable] = useState(() => isSuperOrTenantAdmin())

    const modalProps = {
        okButtonProps: {
            disabled: !hasPermission(isEdit ? PermissionSetMap.EDIT_TOOL_CONFIG : PermissionSetMap.WRITE_TOOLS),
        }
    }

    // useEffect(() => {
    //     if(isShareable){
    //         const { promise, cancel } = ToolsIntegrationsAPI.getAppSecToolsStatus();
    //         promise.then(res => setShareableTools(res.filter(i => i.isShareable).map(i => i.toolName))).catch((e) => console.error(e));
    //         return cancel;
    //     }
    // }, [isShareable])

    const onOk = useCallback(async (data) => {
        if(_.isFunction(props.beforeSave)) {
            data = props.beforeSave(data)
        }
        if(isShareable && (shareableTools.indexOf(props.name) !== -1) &&  !data.shareable){
            data.shareable = false;
        }
        const response = await ToolsIntegrationsAPI[!isEdit ? "postGenericLoginDetail" : "updateGenericLoginDetail"](data, props.name, props.config?.id).promise;
        setSuccess(true);

        if(_.isFunction(props.afterSave)){
            props.afterSave(response);
        }

        // if auto onboarding is supported and not enabled then open mapping form
        if (!isEdit && (AutoMappingSupportedTools.includes(props.name) ? !data.autoOnboardProjects : !props.noMappingForm)) {
            ModalUtil.show({
                content: <props.MappingForm toolName={props.name} loginConfigId={response.id} onUpdateConfigComponent={props.onUpdateConfigComponent} loginConfig={response} {...props.defaultValues} />,
            })
        }
    }, [isEdit, props, isShareable]);

    const fields = useMemo(() => {
        const _fields = [
            ...prependFields,
            {
                name: "name",
                label: "Configuration Name",
                NoDefaultLengthRule: true,
                rules : [getRegex(props.name)],
                props: {
                    ref: nameInputFieldRef
                },
                required: true
            }, ...props.fields, ...(isShareable && (shareableTools.indexOf(props.name) !== -1) ? [{
                name: "shareable",
                label: `Share ${props.name} configuration across all Organizations.`,
                help: 'Once shared, the credential cannot be unshared or deleted',
                type: FieldTypes.SWITCH,
                disabled: isEdit && props.config?.shareable
            }] : []),
            ...(featureFlags.SIMPLIFIED_ONBOARDING && AutoMappingSupportedTools.indexOf(props.name) !== -1 ? [{
                name: "autoOnboardProjects",
                label: 'Auto-Onboard projects where Git Repo is already onboarded',
                type: FieldTypes.SWITCH,
            }] : [])
        ];

        return getFields ? getFields(_fields) : _fields;
    }, [prependFields, props.name, props.fields, props.config?.shareable, nameInputFieldRef, isShareable, isEdit, featureFlags.SIMPLIFIED_ONBOARDING, getFields]);

    function getRegex(toolName) {
        let parameters = {}
        switch(toolName){
            case "Lacework":
                parameters = {max:60};
            break;
            default:
            break;
        }
        return (() => nameRegexValidator(parameters));
    }

    return (
        <BaseModalForm
            ref={ref}
            {...props}
            modalProps={modalProps}
            afterClose={() => props.afterClose(success)}
            title={`${isEdit ? "Edit" : "Add"} ${props.toolDisplayName || props.name} Credentials`}
            onOk={onOk}
            fields={fields}
            initialValues={props.config && _.isFunction(props.parseExtraFields) ? props.parseExtraFields(props.config) :  props.config || {
                ...props.initialValue,
                ...(
                    featureFlags.SIMPLIFIED_ONBOARDING ? {
                        autoOnboardProjects: true
                    } : {}
                )
            }}
        />
    )
})

GenericPullCredentialsForm.propTypes = {
    name: stringPropType.isRequired,
    fields: arrayPropType.isRequired
}

export default GenericPullCredentialsForm;